import { useEffect, useState } from "react";
import { Box, Button, Container, Paper, Stack, Typography } from "@mui/material";
import { supabase } from "../../lib/supabaseClient";

const colors = {
    paper: "rgba(255, 250, 242, 0.94)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.66)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    blue: "#2f91b8",
    blueBg: "rgba(47,145,184,0.11)",
    border: "rgba(155,118,83,0.16)",
    warning: "#9a6a20",
    warningBg: "rgba(154,106,32,0.12)",
    success: "#4f7b58",
    successBg: "rgba(79,123,88,0.12)",
};

const variantStyles = {
    info: {
        label: "Hinweis",
        icon: "ℹ️",
        bg: colors.blueBg,
        color: colors.blue,
    },
    important: {
        label: "Wichtig",
        icon: "⚠️",
        bg: colors.warningBg,
        color: colors.warning,
    },
    season: {
        label: "Saison",
        icon: "✨",
        bg: colors.successBg,
        color: colors.success,
    },
};

async function getSiteNotice() {
    const { data, error } = await supabase
        .from("site_notice")
        .select("*")
        .eq("id", "main")
        .maybeSingle();

    if (error) {
        throw new Error(error.message);
    }

    return data;
}

function SiteNotice() {
    const [notice, setNotice] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function loadNotice() {
            try {
                setIsLoading(true);

                const data = await getSiteNotice();
                setNotice(data);
            } catch (error) {
                console.error("Site notice load error:", error);
            } finally {
                setIsLoading(false);
            }
        }

        loadNotice();
    }, []);

    if (isLoading) {
        return null;
    }

    if (!notice?.active) {
        return null;
    }

    const currentVariant = variantStyles[notice.variant] || variantStyles.info;
    const hasButton = notice.button_label && notice.button_url;

    return (
        <Box
            component="section"
            aria-label="Aktueller Hinweis"
            sx={{
                pt: {
                    xs: 3,
                    md: 4,
                },
                background:
                    "linear-gradient(180deg, #fffaf2 0%, #fffaf2 100%)",
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    maxWidth: 1180,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        p: {
                            xs: 2.4,
                            md: 3,
                        },
                        borderRadius: {
                            xs: 4,
                            md: 5,
                        },
                        backgroundColor: colors.paper,
                        border: `1px solid ${colors.border}`,
                        boxShadow: "0 18px 60px rgba(78,54,30,0.08)",
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        spacing={2}
                        alignItems={{
                            xs: "flex-start",
                            md: "center",
                        }}
                        justifyContent="space-between"
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={1.6}
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 48,
                                    height: 48,
                                    flexShrink: 0,
                                    borderRadius: 3,
                                    backgroundColor: currentVariant.bg,
                                }}
                            >
                                <Typography sx={{ fontSize: 23, lineHeight: 1 }}>
                                    {currentVariant.icon}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        mb: 0.4,
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        fontSize: 12,
                                        fontWeight: 900,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: currentVariant.color,
                                    }}
                                >
                                    {currentVariant.label}
                                </Typography>

                                <Typography
                                    component="h2"
                                    sx={{
                                        mb: 0.5,
                                        fontFamily: '"Quicksand", sans-serif',
                                        fontSize: {
                                            xs: "1.45rem",
                                            md: "1.8rem",
                                        },
                                        lineHeight: 1.1,
                                        fontWeight: 800,
                                        letterSpacing: "-0.045em",
                                        color: colors.ink,
                                    }}
                                >
                                    {notice.title}
                                </Typography>

                                {notice.message && (
                                    <Typography
                                        sx={{
                                            maxWidth: 760,
                                            fontFamily:
                                                '"Nunito Sans", sans-serif',
                                            fontSize: {
                                                xs: 15,
                                                md: 16,
                                            },
                                            lineHeight: 1.6,
                                            color: colors.muted,
                                        }}
                                    >
                                        {notice.message}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>

                        {hasButton && (
                            <Button
                                href={notice.button_url}
                                variant="contained"
                                sx={{
                                    minHeight: 48,
                                    px: 3,
                                    borderRadius: 999,
                                    backgroundColor: colors.brownDark,
                                    color: "#fff",
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 13,
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    whiteSpace: "nowrap",
                                    boxShadow:
                                        "0 14px 30px rgba(111,81,54,0.22)",

                                    "&:hover": {
                                        backgroundColor: colors.brownDarker,
                                        boxShadow:
                                            "0 18px 38px rgba(111,81,54,0.28)",
                                    },
                                }}
                            >
                                {notice.button_label}
                            </Button>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}

export default SiteNotice;